import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { toastSuccess } from '../utils/toasts';
import useGetParams from '../hooks/useGetParams';
import { CreateWireRequestDTO, WireRequestDTO } from '../models/wire';
import {
  useCreateWireRequestMutation, useGetBankAccountsQuery, useUpdateWireRequestMutation, RootState,
} from '../store';
import Input from './atoms/Input';
import Button from './atoms/Button';
import { BankAccountDTO } from '../models/bank-account';
import CurrencyInput from './atoms/CurrencyInput';
import Select from './atoms/Select/Select';

interface CreateEditWireProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  wire?: WireRequestDTO;
  maxToRequest: number;
  superadmin?: boolean;
}

const CreateEditWire = ({
  showModal, setShowModal, wire, maxToRequest, superadmin,
}: CreateEditWireProps) => {
  const { eventId, businessId } = superadmin ? { eventId: '', businessId: '' } : useGetParams();
  const [createWireRequest, resCreateWireRequest] = useCreateWireRequestMutation();
  const [updateWireRequest, resUpdateWireRequest] = useUpdateWireRequestMutation();
  const getValidationSchema = () => yup.object().shape({
    amount: yup.number().required('El monto es requerido').max(maxToRequest, 'El monto no puede superar al saldo y transferencias pendientes').typeError('El monto debe ser un número'),
    bank_account_id: yup.string().required('La cuenta es requerida'),
  });
  const {
    handleSubmit, setValue, formState: { errors }, watch,
  } = useForm<CreateWireRequestDTO>({
    defaultValues: {
      bank_account_id: wire?.bank_account_id,
      amount: wire?.amount,
    },
    resolver: yupResolver(getValidationSchema()),
  });
  const { data: bankAccounts } = useGetBankAccountsQuery({ businessId });
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountDTO | null>(null);
  const userData = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (showModal) {
      setSelectedBankAccount(null);
      setValue('bank_account_id', '');
    }
    if (wire) {
      setSelectedBankAccount(bankAccounts?.find((bankAccount) => bankAccount.id === wire.bank_account_id) || null);
    }
  }, [showModal, setValue, wire]);

  useEffect(() => {
    if (resCreateWireRequest.isSuccess) {
      setShowModal(false);
      toastSuccess('Solicitud de dinero creada');
    }
    if (resUpdateWireRequest.isSuccess) {
      setShowModal(false);
      toastSuccess('Solicitud de dinero actualizada');
    }
  }, [resCreateWireRequest.isSuccess, resUpdateWireRequest.isSuccess, setShowModal]);

  const dropDownData = bankAccounts && bankAccounts.length > 0 ? bankAccounts?.map((item) => ({
    label: `${item.holder_name} - ${item.entity_name}`,
    value: item.id,
  })) : [{ label: 'No hay cuentas disponibles', value: '' }];

  const onSubmit = (formData: CreateWireRequestDTO) => {
    if (!wire) {
      createWireRequest({
        eventId,
        businessId: superadmin ? undefined : businessId,
        wireData: {
          bank_account_id: formData.bank_account_id,
          amount: formData.amount,
        },
      });
    } else {
      updateWireRequest({
        eventId: superadmin ? wire.event_id : eventId,
        businessId: superadmin ? undefined : businessId,
        wireData: {
          id: wire.id,
          bank_account_id: formData.bank_account_id,
          amount: formData.amount,
        },
      });
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-col gap-4 mt-4'>
          <Input
            id='requested_by'
            label='Solicitado por *'
            name='requested_by'
            type='text'
            value={userData?.username}
            labelAbove
            disabled
          />
          <div className='w-full'>
            <Select
              id='bank_account_id'
              label='Cuenta *'
              options={dropDownData || []}
              value={selectedBankAccount?.id}
              onChange={(e) => setValue('bank_account_id', e.target.value)}
            />
            <p className='text-red-500 text-sm'>{errors.bank_account_id?.message}</p>
          </div>
          <CurrencyInput
            id='amount'
            label='Monto a transferir *'
            name='amount'
            labelAbove
            errMsg={errors.amount?.message}
            value={watch('amount')}
            onChange={(value) => setValue('amount', value)}
          />
          <div className='flex flex-row gap-2 items-center'>
            <Button variant='secondary' onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant='primary' type='submit' loading={resCreateWireRequest.isLoading || resUpdateWireRequest.isLoading}>Guardar</Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateEditWire;
